import { Clock, DollarSign, Package, TrendingUp, Plus, X, Check, Edit2, AlertCircle, Trash2, ChevronDown, ChevronUp, Wrench, Tag, FileCheck2, Calendar, GripVertical, Info, Wallet, PlayCircle, Settings2, FileText, CheckCircle2, ChevronRight, Calculator } from "lucide-react";
import TermijnSetup from "./TermijnSetup";
import { Phone, Users } from "lucide-react";
import { useProjectManagementContext } from "../context";
import { isInternalManproProject } from "../isInternalManproProject";
import ProjectSectionShell from "./ProjectSectionShell";
import { useState } from "react";

const PlanningSection = () => {
  const {
    baselineData,
    handleBaselineChange,
    isCleaning,
    setBaselineData,
    setIsInventoryModalOpen,
    setIsVendorModalOpen,
    isBaselineLocked,
    activeProjectId,
    userRole,
    handleSaveBaseline,
    handleSaveBaselineDraft,
    handleFinalizeBaseline,
    isRejectedProject,
    isPendingApproval,
    isActiveApproval,
    activeProjectData,
  } = useProjectManagementContext();

  const isInternalProject = isInternalManproProject(activeProjectData);

  const [activeSubTab, setActiveSubTab] = useState<"identitas" | "wbs" | "anggaran" | "vendor" | "escrow">("identitas");
  const [expandedMilestones, setExpandedMilestones] = useState<number[]>([]);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [teamMemberForm, setTeamMemberForm] = useState({
    name: "",
    role: "PIC Proyek",
    contact: "",
    note: "",
  });
  const [newVendorInput, setNewVendorInput] = useState({ name: "", task: "", cost: "" });
  const [newInventoryInput, setNewInventoryInput] = useState("");

  const planningActionButtons = activeProjectId && userRole === "provider" ? (
    <div className="flex items-center gap-2.5 flex-wrap">
      <button
        disabled={isBaselineLocked || isActiveApproval}
        className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-extrabold rounded-2xl border border-white/40 bg-white/20 text-white hover:bg-white/30 transition-all duration-200 backdrop-blur shadow-sm disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
        onClick={handleSaveBaselineDraft ?? handleSaveBaseline}
      >
        <FileCheck2 size={17} className="flex-shrink-0" />
        <span>Simpan Draft Rencana</span>
      </button>
      <button
        disabled={isBaselineLocked || isActiveApproval}
        className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-extrabold rounded-2xl bg-white text-blue-800 hover:bg-blue-50 hover:shadow-lg transition-all duration-200 shadow-md shadow-black/10 disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
        onClick={handleFinalizeBaseline ?? handleSaveBaseline}
      >
        <FileCheck2 size={17} className="flex-shrink-0" />
        <span>{isRejectedProject ? "Kirim Ulang Revisi" : "Finalisasi Rencana Kerja"}</span>
      </button>
    </div>
  ) : null;

  const projectTeamRoles = [
    "PIC Proyek",
    "Mandor",
    "Tukang",
    "Arsitek/PM",
    "Admin Lapangan",
    "Supplier/Vendor Pendukung",
  ];

  const toggleMilestone = (id: number) => {
    setExpandedMilestones(prev =>
      prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]
    );
  };

  const calculateTotalWeight = () => {
    return baselineData.milestones.reduce(
      (sum: number, m: any) => sum + Number(m.weight || 0),
      0
    );
  };

  const calculateProjectTotal = () => {
    return baselineData.tasks.reduce((sum: number, t: any) => {
      const m = baselineData.milestones.find(
        (ms: any) => ms.id === t.milestone_id
      );
      return (
        sum +
        (m ? (Number(m.weight || 0) * Number(t.weight || 0)) / 100 : 0)
      );
    }, 0);
  };

  const calculateAllocationTotal = () => {
    return baselineData.allocation.reduce((sum: number, item: any) => sum + (Number(item.percentage) || 0), 0);
  };

  const isAllocationValid = calculateAllocationTotal() <= 100;
  const projectTeamMembers = baselineData.teamMembers || [];

  const handleAddTeamMember = () => {
    if (!teamMemberForm.name.trim()) {
      return;
    }

    setBaselineData((prev: any) => ({
      ...prev,
      teamMembers: [
        ...(prev.teamMembers || []),
        {
          id: Date.now(),
          name: teamMemberForm.name.trim(),
          role: teamMemberForm.role,
          contact: teamMemberForm.contact.trim(),
          note: teamMemberForm.note.trim(),
        },
      ],
    }));

    setTeamMemberForm({
      name: "",
      role: "PIC Proyek",
      contact: "",
      note: "",
    });
  };

  const handleRemoveTeamMember = (id: number | string) => {
    setBaselineData((prev: any) => ({
      ...prev,
      teamMembers: (prev.teamMembers || []).filter((member: any) => member.id !== id),
    }));
  };

  return (
    <ProjectSectionShell
      eyebrow="Tahap Persiapan (Pre-Construction)"
      title="Penyusunan RAB, Kurva-S, & Kontrak"
      description="Susun Data Proyek, Jadwal (Kurva-S), Rincian Pekerjaan, RAB, dan Daftar Subkon/Material sebelum SPK diterbitkan."
      icon={<TrendingUp size={14} />}
      tone="blue"
      action={planningActionButtons}
    >
      <div className="space-y-6">
        <div className="flex gap-2 mb-6 bg-slate-100 p-1.5 rounded-2xl w-full border border-slate-200 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden snap-x snap-mandatory">
          <button
            onClick={() => setActiveSubTab("identitas")}
            className={`snap-start flex-none sm:flex-1 py-2 px-4 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${activeSubTab === "identitas" ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:bg-slate-200/50 hover:text-slate-700"}`}
          >
            Data Proyek & Jadwal
          </button>
          <button
            onClick={() => setActiveSubTab("wbs")}
            className={`snap-start flex-none sm:flex-1 py-2 px-4 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${activeSubTab === "wbs" ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:bg-slate-200/50 hover:text-slate-700"}`}
          >
            Uraian Pekerjaan (Kurva-S)
          </button>
          <button
            onClick={() => setActiveSubTab("anggaran")}
            className={`snap-start flex-none sm:flex-1 py-2 px-4 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${activeSubTab === "anggaran" ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:bg-slate-200/50 hover:text-slate-700"}`}
          >
            RAB & Margin
          </button>
          <button
            onClick={() => setActiveSubTab("vendor")}
            className={`snap-start flex-none sm:flex-1 py-2 px-4 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${activeSubTab === "vendor" ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:bg-slate-200/50 hover:text-slate-700"}`}
          >
            Subkon & Alat Kerja
          </button>
          <button
            onClick={() => setActiveSubTab("escrow")}
            className={`snap-start flex-none sm:flex-1 py-2 px-4 rounded-xl text-sm font-bold transition-all whitespace-nowrap ${activeSubTab === "escrow" ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:bg-slate-200/50 hover:text-slate-700"}`}
          >
            {isInternalProject ? "Rencana internal" : "Skema Termin & Retensi"}
          </button>
        </div>

        {activeSubTab === "identitas" && (
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-blue-50 rounded-xl">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-800">Setup Data Proyek & Jadwal</h3>
                <p className="text-sm text-slate-500">Langkah 1</p>
              </div>
            </div>

            <div className="space-y-5">
              {/* Project Name */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Nama Proyek
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="text"
                  disabled={isBaselineLocked}
                  className="w-full border border-slate-300 rounded-xl px-4 py-3 text-slate-800 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-slate-50"
                  placeholder="Contoh: Renovasi Gedung Utama"
                  value={baselineData.projectName || ""}
                  onChange={(e) => handleBaselineChange("projectName", e.target.value)}
                />
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Tgl Mulai (MCO)
                  </label>
                  <input
                    type="date"
                    disabled={isBaselineLocked}
                    className="w-full border border-slate-300 rounded-xl px-4 py-3 text-slate-800 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-slate-50"
                    value={baselineData.kickoff}
                    onChange={(e) => handleBaselineChange("kickoff", e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Tgl Selesai (BAST)
                  </label>
                  <input
                    type="date"
                    disabled={isBaselineLocked}
                    className="w-full border border-slate-300 rounded-xl px-4 py-3 text-slate-800 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-slate-50"
                    value={baselineData.deadline}
                    onChange={(e) => handleBaselineChange("deadline", e.target.value)}
                  />
                </div>
              </div>

              {/* Milestones */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="text-sm font-semibold text-slate-700">
                    Tahapan Pekerjaan Utama
                  </label>
                  <span className={`text-sm font-bold ${calculateTotalWeight() === 100 ? 'text-emerald-600' : 'text-amber-600'}`}>
                    Total: {calculateTotalWeight().toFixed(2)}%
                  </span>
                </div>

                <div className="space-y-3">
                  {baselineData.milestones.map((m: any, idx: number) => (
                    <div
                      key={m.id}
                      className="bg-slate-50 rounded-xl p-4 border border-slate-200 hover:border-blue-300 transition-all"
                    >
                      <div className="flex flex-col md:flex-row md:items-center gap-3">
                        <div className="flex-1 w-full md:w-auto min-w-0">
                          <input
                            type="text"
                            disabled={isBaselineLocked}
                            className="w-full bg-transparent border-0 border-b-2 border-slate-300 px-0 py-1 text-sm font-medium text-slate-800 focus:border-blue-500 focus:ring-0 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                            placeholder="Nama tahapan (misal: Pekerjaan Persiapan)"
                            value={m.label}
                            onChange={(e) =>
                              handleBaselineChange("milestones", e.target.value, idx, "label")
                            }
                          />
                        </div>
                        <div className="flex items-center gap-3 w-full md:w-auto justify-between md:justify-end">
                        <div className="flex items-center gap-2">
                          <input
                            type="date"
                            disabled={isBaselineLocked}
                            className="bg-transparent border-0 border-b-2 border-slate-300 px-0 py-1 text-sm focus:border-blue-500 focus:ring-0 transition-all w-32 disabled:opacity-60 disabled:cursor-not-allowed"
                            value={m.date}
                            min={baselineData.kickoff}
                            max={baselineData.deadline}
                            onChange={(e) =>
                              handleBaselineChange("milestones", e.target.value, idx, "date")
                            }
                          />
                        </div>
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            disabled={isBaselineLocked}
                            className="w-16 bg-white border border-slate-300 rounded-lg px-2 py-1 text-sm font-bold focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-slate-100"
                            value={m.weight}
                            step="any"
                            onChange={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              const currentTotal = calculateTotalWeight();
                              const otherWeights = currentTotal - Number(m.weight || 0);
                              const maxAllowed = Math.max(0, 100 - otherWeights);
                              const finalVal = val > maxAllowed ? maxAllowed : val;
                              handleBaselineChange(
                                "milestones",
                                finalVal,
                                idx,
                                "weight"
                              );
                            }}
                          />
                          <span className="text-sm text-slate-500">%</span>
                        </div>
                        <div className="flex items-center gap-2">
                        {!isBaselineLocked && (
                          <button
                            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                            onClick={() => {
                              const newList = baselineData.milestones.filter(
                                (_: any, mIdx: number) => mIdx !== idx
                              );
                              const equalWeight = newList.length > 0 ? 100 / newList.length : 0;
                              const updatedList = newList.map((m: any) => ({
                                ...m,
                                weight: parseFloat(equalWeight.toFixed(2))
                              }));
                              setBaselineData({ ...baselineData, milestones: updatedList });
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      </div>
                    </div>
                  </div>
                  ))}

                  {!isBaselineLocked && (
                    <button
                      className="w-full py-3 border-2 border-dashed border-slate-300 rounded-xl text-sm font-semibold text-slate-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-all flex items-center justify-center gap-2"
                      onClick={() => {
                        setBaselineData((prev: any) => {
                          const newList = [
                            ...prev.milestones,
                            {
                              id: Date.now(),
                              label: "Tahapan Pekerjaan Baru",
                              date: "",
                              weight: 0,
                            },
                          ];
                          const equalWeight = 100 / newList.length;
                          const updatedList = newList.map((m: any) => ({
                            ...m,
                            weight: parseFloat(equalWeight.toFixed(2))
                          }));
                          return {
                            ...prev,
                            milestones: updatedList,
                          };
                        });
                      }}
                    >
                      <Plus className="w-4 h-4" />
                      Tambah Pekerjaan Utama
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSubTab === "wbs" && (
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-indigo-50 rounded-xl">
                <TrendingUp className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-800">Rincian Pekerjaan & Bobot (%)</h3>
                <p className="text-sm text-slate-500">Langkah 2</p>
              </div>
            </div>

            <div className="space-y-4">
              {baselineData.milestones.length === 0 ? (
                <div className="text-center py-8">
                  <div className="bg-slate-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                    <AlertCircle className="w-6 h-6 text-slate-400" />
                  </div>
                  <p className="text-sm text-slate-500 italic">
                    Tambah Pekerjaan Utama di Langkah 1 terlebih dahulu
                  </p>
                </div>
              ) : (
                baselineData.milestones.map((milestone: any) => {
                  const milestoneTasks = baselineData.tasks.filter(
                    (t: any) => t.milestone_id === milestone.id
                  );
                  const milestoneWeight = milestoneTasks.reduce(
                    (sum: number, t: any) => sum + Number(t.weight || 0),
                    0
                  );
                  const isExpanded = expandedMilestones.includes(milestone.id);

                  return (
                    <div
                      key={milestone.id}
                      className="border border-slate-200 rounded-xl overflow-hidden transition-all"
                    >
                      {/* Milestone Header */}
                      <div
                        className="flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 cursor-pointer transition-all"
                        onClick={() => toggleMilestone(milestone.id)}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${milestoneWeight === 100 ? 'bg-emerald-500' : 'bg-amber-500'}`} />
                              <span className="font-semibold text-slate-800">
                                {milestone.label || "Tahapan Tanpa Nama"}
                              </span>
                            </div>
                            <span className="text-xs text-slate-500">
                              Bobot Tahapan: {Number(milestone.weight).toFixed(2)}%
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className={`text-xs font-semibold px-2 py-1 rounded-lg ${
                            milestoneWeight === 100 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {Number(milestoneWeight).toFixed(2)}%
                          </span>
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4 text-slate-400" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-slate-400" />
                          )}
                        </div>
                      </div>

                      {/* Milestone Content */}
                      {isExpanded && (
                        <div className="p-4 space-y-3">
                          <div className="overflow-x-auto w-full">
                            <table className="w-full min-w-[500px]">
                            <thead>
                              <tr className="text-xs text-slate-500">
                                <th className="text-left font-medium pb-2">Sub-Pekerjaan</th>
                                <th className="text-center font-medium pb-2 w-20">Bobot</th>
                                <th className="text-right font-medium pb-2 w-24">Kontribusi</th>
                                <th className="text-right font-medium pb-2 w-12"></th>
                              </tr>
                            </thead>
                            <tbody className="space-y-2">
                              {milestoneTasks.map((task: any) => {
                                const contribution = (Number(milestone.weight) * Number(task.weight)) / 100;
                                return (
                                  <tr key={task.id} className="group">
                                    <td className="py-1.5">
                                      <input
                                        type="text"
                                        disabled={isBaselineLocked}
                                        className="w-full bg-transparent border-0 border-b-2 border-slate-200 px-0 py-1 text-sm focus:border-blue-500 focus:ring-0 transition-all group-hover:border-slate-300 disabled:opacity-60 disabled:cursor-not-allowed"
                                        value={task.name}
                                        onChange={(e) => {
                                          const idx = baselineData.tasks.findIndex(
                                            (t: any) => t.id === task.id
                                          );
                                          handleBaselineChange("tasks", e.target.value, idx, "name");
                                        }}
                                      />
                                    </td>
                                    <td className="py-1.5">
                                      <div className="flex items-center justify-center gap-1">
                                        <input
                                          type="number"
                                          disabled={isBaselineLocked}
                                          className="w-16 text-center bg-white border border-slate-300 rounded-lg px-2 py-1 text-sm font-bold focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:opacity-60 disabled:bg-slate-50 disabled:cursor-not-allowed"
                                          value={task.weight}
                                          step="any"
                                          onChange={(e) => {
                                            const val = parseFloat(e.target.value) || 0;
                                            const otherTasksInMilestone = baselineData.tasks.filter(
                                              (t: any) => t.milestone_id === milestone.id && t.id !== task.id
                                            );
                                            const otherWeights = otherTasksInMilestone.reduce(
                                              (sum: number, t: any) => sum + Number(t.weight || 0), 0
                                            );
                                            const maxAllowed = Math.max(0, 100 - otherWeights);
                                            const finalVal = val > maxAllowed ? maxAllowed : val;
                                            
                                            const idx = baselineData.tasks.findIndex(
                                              (t: any) => t.id === task.id
                                            );
                                            handleBaselineChange(
                                              "tasks",
                                              finalVal,
                                              idx,
                                              "weight"
                                            );
                                          }}
                                        />
                                        <span className="text-xs text-slate-400">%</span>
                                      </div>
                                    </td>
                                    <td className="text-right font-mono text-sm font-semibold text-slate-700">
                                      {Number(contribution).toFixed(2)}%
                                    </td>
                                    <td className="text-right">
                                      {!isBaselineLocked && (
                                        <button
                                          className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                                          onClick={() => {
                                            const newTasks = baselineData.tasks.filter(
                                              (t: any) => t.id !== task.id
                                            );
                                            const sameMilestoneTasks = newTasks.filter((t: any) => t.milestone_id === milestone.id);
                                            const equalWeight = sameMilestoneTasks.length > 0 ? 100 / sameMilestoneTasks.length : 0;
                                            
                                            const updatedTasks = newTasks.map((t: any) => 
                                              t.milestone_id === milestone.id 
                                                ? { ...t, weight: parseFloat(equalWeight.toFixed(2)) }
                                                : t
                                            );
                                            setBaselineData({ ...baselineData, tasks: updatedTasks });
                                          }}
                                        >
                                          <X className="w-4 h-4" />
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                            </table>
                          </div>

                          {!isBaselineLocked && (
                            <button
                              className="w-full py-2 border-2 border-dashed border-slate-200 rounded-lg text-sm font-medium text-slate-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-all flex items-center justify-center gap-2"
                              onClick={() => {
                                setBaselineData((prev: any) => {
                                  const newTasks = [
                                    ...prev.tasks,
                                    {
                                      id: Date.now(),
                                      milestone_id: milestone.id,
                                      name: "Sub-Pekerjaan Baru",
                                      weight: 0,
                                    },
                                  ];
                                  const sameMilestoneTasks = newTasks.filter((t: any) => t.milestone_id === milestone.id);
                                  const equalWeight = 100 / sameMilestoneTasks.length;
                                  
                                  const updatedTasks = newTasks.map((t: any) => 
                                    t.milestone_id === milestone.id 
                                      ? { ...t, weight: parseFloat(equalWeight.toFixed(2)) }
                                      : t
                                  );
                                  return {
                                    ...prev,
                                    tasks: updatedTasks,
                                  };
                                });
                              }}
                            >
                              <Plus className="w-4 h-4" />
                              Tambah Sub-Pekerjaan
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}

              {/* Project Total */}
              {baselineData.milestones.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Total Bobot Kurva-S (Harus 100%)</span>
                    <span className={`text-xl font-bold ${
                      Math.abs(calculateProjectTotal() - 100) < 0.1
                        ? 'text-emerald-600'
                        : 'text-red-500'
                    }`}>
                      {calculateProjectTotal().toFixed(2)}%
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1 italic">
                    * Kontribusi = (Bobot Tahapan × Bobot Sub-Pekerjaan) / 100
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeSubTab === "anggaran" && (
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-emerald-50 rounded-xl">
                <DollarSign className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-800">Rencana Anggaran Biaya (RAB) & Overhead</h3>
                <p className="text-sm text-slate-500">Langkah 3</p>
              </div>
            </div>

            <div className="space-y-5">
              {/* Budget */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Nilai Kontrak (RAB)
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">Rp</span>
                  <input
                    type="text"
                    disabled={isBaselineLocked}
                    className="w-full border border-slate-300 rounded-xl px-4 py-3 pl-10 text-slate-800 text-sm font-semibold focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-slate-50"
                    value={new Intl.NumberFormat("id-ID", {
                      style: "currency",
                      currency: "IDR",
                      maximumFractionDigits: 0,
                    }).format(baselineData.budget)}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^0-9]/g, "");
                      handleBaselineChange("budget", parseInt(val) || 0);
                    }}
                  />
                </div>
              </div>

              {/* Allocations */}
              <div className="space-y-4">
                {baselineData.allocation.map((item: any, index: number) => (
                  <div key={item.id} className="space-y-2 relative group">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-2 w-full sm:flex-1">
                        {!item.isDefault && !isBaselineLocked && (
                          <button
                            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                            onClick={() => {
                              const newAllocations = baselineData.allocation.filter((_: any, i: number) => i !== index);
                              setBaselineData({ ...baselineData, allocation: newAllocations });
                            }}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                        {item.isDefault ? (
                          <span className="text-sm font-medium text-slate-700">{item.name}</span>
                        ) : (
                          <input
                            type="text"
                            disabled={isBaselineLocked}
                            placeholder="Alokasi Biaya (Bahan/Upah/dll)"
                            className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                            value={item.name}
                            onChange={(e) => {
                              const newAllocations = [...baselineData.allocation];
                              newAllocations[index] = { ...newAllocations[index], name: e.target.value };
                              setBaselineData({ ...baselineData, allocation: newAllocations });
                            }}
                          />
                        )}
                      </div>
                      <div className="flex items-center gap-2 w-full sm:w-auto mt-1 sm:mt-0">
                        <div className="flex items-center gap-1 w-1/3 sm:w-auto">
                          <input
                            type="number"
                            disabled={isBaselineLocked}
                            className="w-full sm:w-16 text-right bg-slate-100 border-0 rounded-lg px-2 py-1 text-sm font-bold focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                            value={item.percentage}
                            step="any"
                            onChange={(e) => {
                              const newAllocations = [...baselineData.allocation];
                              newAllocations[index] = { ...newAllocations[index], percentage: parseFloat(e.target.value) || 0 };
                              setBaselineData({ ...baselineData, allocation: newAllocations });
                            }}
                          />
                          <span className="text-xs text-slate-500">%</span>
                        </div>
                        <div className={`flex items-center gap-1 flex-1 sm:flex-none border border-slate-300 rounded-lg px-2 transition-all ${isBaselineLocked ? 'bg-slate-100 opacity-60' : 'bg-slate-50 focus-within:ring-2 focus-within:ring-blue-500 focus-within:bg-white'}`}>
                          <span className="text-xs text-slate-500">Rp</span>
                          <input
                            type="text"
                            disabled={isBaselineLocked}
                            className="w-full sm:w-28 text-right bg-transparent border-0 py-1 text-sm font-bold font-mono focus:ring-0 disabled:cursor-not-allowed"
                            value={new Intl.NumberFormat("id-ID").format(
                              Math.round((baselineData.budget * (item.percentage || 0)) / 100)
                            )}
                            onChange={(e) => {
                              const val = parseInt(e.target.value.replace(/[^0-9]/g, "")) || 0;
                              const maxVal = Math.min(val, baselineData.budget); // Prevent going over total budget
                              const newPercentage = baselineData.budget > 0 ? (maxVal / baselineData.budget) * 100 : 0;
                              const newAllocations = [...baselineData.allocation];
                              newAllocations[index] = { ...newAllocations[index], percentage: newPercentage };
                              setBaselineData({ ...baselineData, allocation: newAllocations });
                            }}
                          />
                        </div>
                      </div>
                    </div>
                    <input
                      type="range"
                      disabled={isBaselineLocked}
                      className="w-full h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600 disabled:opacity-60 disabled:cursor-not-allowed"
                      min="0"
                      max="100"
                      step="any"
                      value={item.percentage}
                      onChange={(e) => {
                        const newAllocations = [...baselineData.allocation];
                        newAllocations[index] = { ...newAllocations[index], percentage: parseFloat(e.target.value) || 0 };
                        setBaselineData({ ...baselineData, allocation: newAllocations });
                      }}
                    />
                  </div>
                ))}
                <div className="flex justify-end pt-2">
                  {!isBaselineLocked && (
                    <button
                      className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1.5 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-lg transition-all"
                      onClick={() => {
                        const newAllocations = [
                          ...baselineData.allocation,
                          { id: Date.now().toString(), name: '', percentage: 0, isDefault: false }
                        ];
                        setBaselineData({ ...baselineData, allocation: newAllocations });
                      }}
                    >
                      <Plus className="w-4 h-4" />
                      Tambah Alokasi
                    </button>
                  )}
                </div>
              </div>

              {/* Summary */}
              <div className="pt-4 border-t border-slate-200 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Total Alokasi Biaya Langsung</span>
                  <span className={`font-bold ${!isAllocationValid ? 'text-red-500' : 'text-slate-800'}`}>
                    {calculateAllocationTotal().toFixed(2)}%
                  </span>
                </div>

                <div className={`p-4 rounded-xl ${isAllocationValid ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-xs text-slate-500">Estimasi Profit / Overhead</p>
                      <p className={`text-xl font-bold ${isAllocationValid ? 'text-emerald-600' : 'text-red-500'}`}>
                        {new Intl.NumberFormat("id-ID", {
                          style: "currency",
                          currency: "IDR",
                          maximumFractionDigits: 0,
                        }).format(
                          (baselineData.budget * (100 - calculateAllocationTotal())) / 100
                        )}
                      </p>
                    </div>
                    <div className={`p-2 rounded-xl ${isAllocationValid ? 'bg-emerald-200' : 'bg-red-200'}`}>
                      <TrendingUp className={`w-6 h-6 ${isAllocationValid ? 'text-emerald-700' : 'text-red-700'}`} />
                    </div>
                  </div>
                  <p className="text-xs mt-2 text-slate-500">
                    * {isAllocationValid ? 'Alokasi biaya langsung masih dalam batas wajar' : 'Alokasi melebihi 100%'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSubTab === "vendor" && (
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-purple-50 rounded-xl">
                <Package className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-800">Manajemen Subkon & Logistik</h3>
                <p className="text-sm text-slate-500">Langkah 4</p>
              </div>
            </div>

            <div className="space-y-5">
              {/* Vendors */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm font-semibold text-slate-700">Daftar Subkon & Supplier Logistik</span>
                </div>

                <div className="space-y-2">
                  {baselineData.vendors.map((v: any, idx: number) => (
                    <div
                      key={v.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-200 hover:border-purple-300 transition-all"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3">
                          <span className="font-medium text-slate-800">{v.name}</span>
                          <span className="text-xs text-slate-500">{v.task}</span>
                        </div>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="text-xs font-medium text-slate-600">
                            {new Intl.NumberFormat("id-ID", {
                              style: "currency",
                              currency: "IDR",
                              maximumFractionDigits: 0,
                            }).format(v.cost || 0)}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {!isBaselineLocked && (
                          <button
                            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                            onClick={() => {
                              const newList = baselineData.vendors.filter(
                                (_: any, vIdx: number) => vIdx !== idx
                              );
                              setBaselineData({ ...baselineData, vendors: newList });
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Inline Add Vendor Form */}
                  {!isBaselineLocked && (
                    <div className="flex flex-col md:flex-row gap-2 mt-3 p-3 bg-white border border-dashed border-slate-300 rounded-xl items-center focus-within:border-blue-400 focus-within:ring-1 focus-within:ring-blue-400 transition-all">
                      <div className="flex-1 w-full grid grid-cols-1 md:grid-cols-3 gap-2">
                        <input 
                          type="text" 
                          placeholder="Nama Subkon/Supplier"
                          className="w-full bg-slate-50 border-0 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white"
                          value={newVendorInput.name}
                          onChange={(e) => setNewVendorInput({ ...newVendorInput, name: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && newVendorInput.name && newVendorInput.cost) {
                              const newList = [
                                ...baselineData.vendors,
                                {
                                  id: Date.now(),
                                  name: newVendorInput.name,
                                  task: newVendorInput.task,
                                  cost: Number(newVendorInput.cost) || 0,
                                  status: "Pending",
                                }
                              ];
                              setBaselineData({ ...baselineData, vendors: newList });
                              setNewVendorInput({ name: "", task: "", cost: "" });
                            }
                          }}
                        />
                        <input 
                          type="text" 
                          placeholder="Peran/Tugas (Opsional)"
                          className="w-full bg-slate-50 border-0 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white"
                          value={newVendorInput.task}
                          onChange={(e) => setNewVendorInput({ ...newVendorInput, task: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && newVendorInput.name && newVendorInput.cost) {
                              const newList = [
                                ...baselineData.vendors,
                                {
                                  id: Date.now(),
                                  name: newVendorInput.name,
                                  task: newVendorInput.task,
                                  cost: Number(newVendorInput.cost) || 0,
                                  status: "Pending",
                                }
                              ];
                              setBaselineData({ ...baselineData, vendors: newList });
                              setNewVendorInput({ name: "", task: "", cost: "" });
                            }
                          }}
                        />
                        <div className="relative w-full">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">Rp</span>
                          <input 
                            type="text" 
                            placeholder="Biaya"
                            className="w-full bg-slate-50 border-0 rounded-lg px-3 py-2 pl-9 text-sm focus:ring-2 focus:ring-blue-500 focus:bg-white"
                            value={newVendorInput.cost ? new Intl.NumberFormat("id-ID").format(Number(newVendorInput.cost)) : ""}
                            onChange={(e) => {
                              const val = e.target.value.replace(/[^0-9]/g, "");
                              setNewVendorInput({ ...newVendorInput, cost: val });
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && newVendorInput.name && newVendorInput.cost) {
                                const newList = [
                                  ...baselineData.vendors,
                                  {
                                    id: Date.now(),
                                    name: newVendorInput.name,
                                    task: newVendorInput.task,
                                    cost: Number(newVendorInput.cost) || 0,
                                    status: "Pending",
                                  }
                                ];
                                setBaselineData({ ...baselineData, vendors: newList });
                                setNewVendorInput({ name: "", task: "", cost: "" });
                              }
                            }}
                          />
                        </div>
                      </div>
                      <button
                        className="px-4 py-2 w-full md:w-auto text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all disabled:opacity-50"
                        disabled={!newVendorInput.name || !newVendorInput.cost}
                        onClick={() => {
                          const newList = [
                            ...baselineData.vendors,
                            {
                              id: Date.now(),
                              name: newVendorInput.name,
                              task: newVendorInput.task,
                              cost: Number(newVendorInput.cost) || 0,
                              status: "Pending",
                            }
                          ];
                          setBaselineData({ ...baselineData, vendors: newList });
                          setNewVendorInput({ name: "", task: "", cost: "" });
                        }}
                      >
                        Tambah
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Inventory */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <div>
                    <span className="text-sm font-semibold text-slate-800">Mobilisasi Alat Berat & Perkakas</span>
                    <p className="text-xs text-slate-500">Daftarkan alat berat dan perkakas kerja yang akan dimobilisasi ke lapangan</p>
                  </div>
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                    {baselineData.inventory.length} Alat Terdaftar
                  </span>
                </div>

                <div className="space-y-3">
                  {/* Dedicated Input Bar */}
                  {!isBaselineLocked && (
                    <div className="flex items-center gap-2 p-1.5 bg-white border-2 border-slate-200 rounded-xl shadow-sm focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-50 transition-all">
                      <div className="pl-2.5 text-slate-400">
                        <Wrench className="w-4 h-4 text-blue-600" />
                      </div>
                      <input
                        type="text"
                        className="flex-1 bg-transparent text-sm text-slate-800 placeholder-slate-400 focus:ring-0 px-2 py-1 font-medium"
                        placeholder="Ketik nama alat berat/perkakas baru..."
                        value={newInventoryInput}
                        onChange={(e) => setNewInventoryInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === ",") {
                            e.preventDefault();
                            const val = newInventoryInput.trim();
                            if (val) {
                              const newList = [
                                ...baselineData.inventory,
                                {
                                  id: Date.now(),
                                  name: val,
                                  status: "Pending",
                                }
                              ];
                              setBaselineData({ ...baselineData, inventory: newList });
                              setNewInventoryInput("");
                            }
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const val = newInventoryInput.trim();
                          if (val) {
                            const newList = [
                              ...baselineData.inventory,
                              {
                                id: Date.now(),
                                name: val,
                                status: "Pending",
                              }
                            ];
                            setBaselineData({ ...baselineData, inventory: newList });
                            setNewInventoryInput("");
                          }
                        }}
                        disabled={!newInventoryInput.trim()}
                        className="px-3 py-1.5 text-xs font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:hover:bg-blue-600 transition-all flex items-center gap-1.5 shadow-sm"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Tambah
                        <kbd className="hidden sm:inline-block px-1.5 py-0.5 text-[10px] font-mono bg-blue-700 text-blue-100 rounded border border-blue-500/50 ml-1">↵ Enter</kbd>
                      </button>
                    </div>
                  )}

                  {/* Inventory Tags Container */}
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 min-h-[80px]">
                    {baselineData.inventory.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-3 text-center">
                        <Tag className="w-6 h-6 text-slate-300 mb-1" />
                        <p className="text-xs font-medium text-slate-500">Belum ada alat berat/perkakas ditambahkan.</p>
                        {!isBaselineLocked && (
                          <p className="text-[11px] text-slate-400 mt-0.5">Ketik nama alat pada kolom di atas lalu tekan <kbd className="px-1 py-0.5 bg-slate-200 text-slate-600 rounded text-[10px]">Enter ↵</kbd></p>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {baselineData.inventory.map((inv: any, idx: number) => (
                          <div key={inv.id} className="group flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 hover:border-blue-300 rounded-lg shadow-sm transition-all">
                            <span className="w-2 h-2 rounded-full bg-blue-500" />
                            <span className="text-sm font-semibold text-slate-700">{inv.name}</span>
                            {!isBaselineLocked && (
                              <button
                                className="text-slate-400 hover:text-red-500 rounded-md p-0.5 hover:bg-red-50 transition-colors ml-1"
                                title="Hapus alat"
                                onClick={() => {
                                  const newList = baselineData.inventory.filter((_: any, iIdx: number) => iIdx !== idx);
                                  setBaselineData({ ...baselineData, inventory: newList });
                                }}
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-xl border border-blue-200">
                  <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-blue-700">
                    Selesaikan validasi subkon & material sebelum me-launch baseline
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSubTab === "escrow" && (
          isInternalProject ? (
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-slate-100 rounded-xl">
                  <Wallet className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-800">Rencana internal (opsional)</h3>
                  <p className="text-sm text-slate-500">Tidak dipakai sebagai tagihan klien atau escrow.</p>
                </div>
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">
                Proyek ini tanpa klien di platform. Finalisasi RAB tidak membuat jadwal transfer atau pencairan escrow. Anggaran di tab RAB cukup sebagai acuan kerja.
              </p>
            </div>
          ) : (
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-blue-50 rounded-xl">
                <Wallet className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-800">Skema Termin Pembayaran & Retensi</h3>
                <p className="text-sm text-slate-500">Pengaturan tahapan pembayaran (Progress) dan nilai penahanan dana untuk Masa Pemeliharaan (Retensi)</p>
              </div>
            </div>
            <TermijnSetup />
          </div>
          )
        )}
      </div>
    </ProjectSectionShell>
  );
};

export default PlanningSection;
